import { Box, CircularProgress, Dialog, IconButton, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import NorthEastIcon from '@mui/icons-material/NorthEast'
import SouthEastIcon from '@mui/icons-material/SouthEast'
import EastIcon from '@mui/icons-material/East'
import { useContext, useEffect, useRef, useState } from 'react'
import Context from '../Context'
import { useClock } from '../utils/clock'
import theme from '../theme'
import { formatDecimalPrice } from '../utils/price'
import { formatMargin } from '../utils/margin'
import { displayBookmaker } from '../utils/arb'
import { apiFetch } from '../utils/api'

const FONT = '"Source Sans Pro", -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif'

const cellSx = { padding: '7px 12px', fontSize: '14px', whiteSpace: 'nowrap' }
// the list opens at its end, which would leave the headings scrolled off the top, so they stay put
const headSx = {
  ...cellSx,
  fontSize: '11px',
  fontWeight: 700,
  letterSpacing: '0.5px',
  textTransform: 'uppercase',
  color: '#9A9EA3',
  textAlign: 'left',
  position: 'sticky',
  top: 0,
  backgroundColor: theme.palette.background.paper,
  zIndex: 1
}



// the book answers in whole dollars of risk, and a stake reads as money or not at all
const formatStake = (amount) => {
  if (amount === null || amount === undefined || !Number.isFinite(amount)) {
    return '—'
  }
  return `$${Math.round(amount).toLocaleString('en-US')}`
}

/**
 * The event screen was handed the book's key along with the price. The grid was not — that feed does not
 * publish keys — so it sends the coordinates instead and the server finds the key in the snapshot it
 * already holds. Either way one request, and null when there is nothing to ask with at all.
 */
/**
 * A max stake only means something for a book we could actually bet into at that price. Bovada's prices
 * are emulated, and Betfair is an exchange quoting lay odds, where the number would answer a different
 * question. For those two the field is not empty — it has no business being on the screen.
 */
const NO_MAX_STAKE = /bovada|betfair/i
const hasMaxStake = (bookmaker) => !NO_MAX_STAKE.test(String(bookmaker || ''))

const maxStakeParams = (request) => {
  if (!request || !hasMaxStake(request.bookmaker)) {
    return null
  }
  if (request.priceKey) {
    return new URLSearchParams({ key: request.priceKey })
  }
  if (!request.home || !request.away || !request.runnerKey || !request.bookmaker) {
    return null
  }
  return new URLSearchParams({
    home: request.home,
    away: request.away,
    koTime: String(request.koTime ?? ''),
    bookmaker: request.bookmaker,
    period: request.period || 'GAME',
    runnerKey: request.runnerKey
  })
}

function Stat({ label, children, title }) {
  return (
    <Box title={title} sx={{ minWidth: 0 }}>
      <Typography sx={{ fontFamily: FONT, fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.6px', textTransform: 'uppercase', color: '#9A9EA3' }}>
        {label}
      </Typography>
      <Typography sx={{ fontFamily: FONT, fontSize: '16px', fontWeight: 600, color: theme.palette.primary.main, fontVariantNumeric: 'tabular-nums' }}>
        {children}
      </Typography>
    </Box>
  )
}

function Trend({ previous, price }) {
  if (previous === undefined) {
    return <EastIcon sx={{ fontSize: 18, color: '#C4C7CA' }} />
  }
  if (price > previous) {
    return <NorthEastIcon sx={{ fontSize: 18, color: theme.palette.primary.light }} />
  }
  if (price < previous) {
    return <SouthEastIcon sx={{ fontSize: 18, color: theme.palette.secondary.main }} />
  }
  return <EastIcon sx={{ fontSize: 18, color: '#C4C7CA' }} />
}

function PriceHistoryModal({ request, onClose }) {
  const { marketHost, usPrices } = useContext(Context)
  const clock = useClock()

  const [ points, setPoints ] = useState(null)
  const [ error, setError ] = useState('')
  const [ stake, setStake ] = useState(null)
  const listRef = useRef(null)

  useEffect(() => {
    if (!request) {
      return
    }
    let cancelled = false
    setPoints(null)
    setError('')

    // only the bookmaker whose price was clicked, there is nothing to switch between.
    // the period matters on the event screen, where the same runner key exists per half and quarter
    const params = new URLSearchParams({
      eventKey: request.eventKey,
      runnerKey: request.runnerKey,
      bookmaker: request.bookmaker,
      period: request.period || 'GAME'
    })
    apiFetch(marketHost, `/api/price-history?${params}`)
      .then(async (response) => {
        // without this a 500 parses fine, has no books, and gets dressed up as "this price never moved"
        if (!response.ok) {
          throw new Error(`history request failed with ${response.status}`)
        }
        return response.json()
      })
      .then((data) => {
        if (cancelled) {
          return
        }
        setPoints((data.books && data.books[request.bookmaker]) || [])
      })
      .catch(() => {
        if (!cancelled) {
          setError('History server is unreachable')
        }
      })

    return () => { cancelled = true }
  }, [request, marketHost])

  /**
   * The max stake is asked for once, when the price is opened. It is a live call to the book rather than
   * something we hold, so it does not belong on the two second poll that keeps the rest of the screen
   * fresh — and it can only be asked at all where the feed handed us the book's key for this price.
   */
  useEffect(() => {
    const params = maxStakeParams(request)
    if (!params) {
      setStake(null)
      return
    }
    let cancelled = false
    setStake({ status: 'loading' })

    apiFetch(marketHost, `/api/max-stake?${params}`)
      .then((response) => response.json())
      .then((data) => {
        if (cancelled) {
          return
        }
        if (data.error || !Number.isFinite(data.maxStake)) {
          setStake({ status: 'error', message: data.error || data.message || 'The book returned no stake' })
          return
        }
        setStake({ status: 'done', value: data.maxStake, message: data.message })
      })
      .catch(() => {
        if (!cancelled) {
          setStake({ status: 'error', message: 'Max stake service is unreachable' })
        }
      })

    return () => { cancelled = true }
  }, [request, marketHost])

  // The list runs oldest first, so on a long history the price that matters — the one on offer now —
  // opens hundreds of rows below the fold. Land on the end of it instead of the beginning.
  useEffect(() => {
    if (points && listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight
    }
  }, [points])

  if (!request) {
    return null
  }

  // Belt and braces on top of the writer only storing changes: whatever ends up in the table, a row that
  // repeats the price above it is not a change and has no business in a history of changes.
  const recorded = (points || []).filter((point, i, all) => i === 0 || point.price !== all[i - 1].price)

  // nothing recorded yet just means this price has not moved since we started watching, which reads
  // better as a single row holding the price it is on than as an apology
  const changes = points !== null && !recorded.length
    ? [{ ts: Date.now(), price: request.price }]
    : recorded

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { fontFamily: FONT, borderRadius: '3px' } }}>
      {/* one fact per cell rather than a single run on line: book, market and selection are three
          different things and reading them apart is the whole point of this screen */}
      <Box sx={{ padding: '16px 16px 14px', borderBottom: '1px solid #E4E4E8' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="h4" sx={{ color: theme.palette.primary.main }}>Price history</Typography>
            <Typography sx={{ fontFamily: FONT, fontSize: '14.5px', fontWeight: 600, color: '#3C4045' }}>
              {request.home && request.away ? `${request.home} — ${request.away}` : request.label}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '12px 16px', marginTop: '14px' }}>
          <Stat label="Book">{displayBookmaker(request.bookmaker)}</Stat>
          <Stat label="Market">
            {request.market || '—'}
            {request.periodLabel && (
              <Box component="span" sx={{ fontSize: '12.5px', fontWeight: 400, color: '#9A9EA3' }}> · {request.periodLabel}</Box>
            )}
          </Stat>
          <Stat label="Selection">{request.selection || request.label}</Stat>
          <Stat label="Current price">{formatDecimalPrice(request.price, usPrices)}</Stat>
          <Stat label="Market margin" title="Implied probabilities of the whole market, less one. Negative on a lay book.">
            {formatMargin(request.margin)}
          </Stat>
          {/* the check runs against the book itself and can come back with a reason instead of a number,
              an account it cannot route through being the usual one. A reason is not a stake, so it is
              said plainly rather than dressed up as a dash that reads like zero. */}
          {hasMaxStake(request.bookmaker) && (
            <Stat label="Max stake" title={stake && stake.message ? stake.message : undefined}>
              {!stake && <Box component="span" sx={{ color: '#C4C7CA' }}>—</Box>}
              {stake && stake.status === 'loading' && <CircularProgress size={15} sx={{ marginTop: '4px' }} />}
              {stake && stake.status === 'error' && (
                <Box component="span" sx={{ fontSize: '13px', fontWeight: 400, color: '#9A9EA3' }}>Unavailable</Box>
              )}
              {stake && stake.status === 'done' && formatStake(stake.value)}
            </Stat>
          )}
        </Box>
      </Box>

      <Box ref={listRef} sx={{ padding: '0 16px 20px', maxHeight: '52vh', overflowY: 'auto' }}>
        {points === null && !error && (
          <Box sx={{ display: 'flex', justifyContent: 'center', padding: '30px' }}><CircularProgress size={26} /></Box>
        )}
        {error && <Typography sx={{ fontFamily: FONT, color: theme.palette.secondary.main }}>{error}</Typography>}
        {points !== null && changes.length > 0 && (
          <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
            <Box component="thead">
              <Box component="tr">
                <Box component="th" sx={headSx}>№</Box>
                <Box component="th" sx={headSx}>Date</Box>
                <Box component="th" sx={headSx}>Time</Box>
                <Box component="th" sx={headSx}>Price</Box>
                <Box component="th" sx={headSx} />
              </Box>
            </Box>
            <Box component="tbody">
              {changes.map((point, index) => (
                <Box component="tr" key={`${point.ts}-${index}`} sx={{ borderBottom: '1px solid #F0F0F3' }}>
                  <Box component="td" sx={{ ...cellSx, color: '#9A9EA3' }}>{index + 1}</Box>
                  <Box component="td" sx={{ ...cellSx, color: '#6E7378' }}>{clock.date(point.ts)}</Box>
                  <Box component="td" sx={{ ...cellSx, fontVariantNumeric: 'tabular-nums' }}>{clock.seconds(point.ts)}</Box>
                  <Box component="td" sx={{ ...cellSx, fontWeight: 'bold', fontVariantNumeric: 'tabular-nums' }}>
                    {formatDecimalPrice(point.price, usPrices)}
                  </Box>
                  <Box component="td" sx={{ ...cellSx, width: '32px' }}>
                    <Trend previous={index === 0 ? undefined : changes[index - 1].price} price={point.price} />
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </Dialog>
  )
}

export default PriceHistoryModal
